import { useState, useCallback, useEffect } from 'react';
import { SessionService } from '../lib/corel/actions/session';
import { sendToTerminal } from '../lib/terminalLogger';
import { TokenService } from '../lib/tokenService';
import { corelCommunication } from '../lib/corel/communication';
import { Header } from './DebuggerModal/components/Header';
import { JsonEditor } from './DebuggerModal/components/JsonEditor';
import { ActionBar } from './DebuggerModal/components/ActionBar';
import { ResponsePane } from './DebuggerModal/components/ResponsePane';
import { ExportModeToggle } from './DebuggerModal/components/ExportModeToggle';
import { Footer } from './DebuggerModal/components/Footer';
import { NormalizeModal } from './DebuggerModal/components/NormalizeModal';

interface DebuggerPageProps {
  onClose: () => void;
}

interface DebugResponse {
  requestId: string;
  status: 'ok' | 'erro' | 'progress_update';
  message?: string;
  result?: any;
  timestamp: string;
}

interface NormalizedAction {
  action: string;
  data: Record<string, any>;
  requestId?: string;
}

interface ValidationResult {
  valid: boolean;
  error?: string;
  normalized?: NormalizedAction;
  warnings?: string[];
}

export function DebuggerPage({ onClose }: DebuggerPageProps) {
  const [jsonInput, setJsonInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastResponse, setLastResponse] = useState<DebugResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [exportSnapshotEnabled, setExportSnapshotEnabled] = useState(false);
  const [showNormalize, setShowNormalize] = useState(false);
  const [skipCache, setSkipCache] = useState(() => {
    // Carregar preferência do localStorage, padrão: true (sempre usar código fresco)
    try {
      const saved = localStorage.getItem('debugger:skipCache');
      return saved !== null ? saved === 'true' : true;
    } catch {
      return true;
    }
  });

  // Exemplo padrão com formato completo
  const defaultExample = {
    action: "corelworks:tool",
    requestId: "req-bc-flags-001",
    data: {
      sessionToken: "<TOKEN>",
      auth: { 
        nonce: "...", 
        timestamp: 1736179200, 
        signature: "..." 
      },
      toolId: "Bitmap.Convert",
      dryRun: false,
      options: {
        colorMode: "RGB",
        transparent: true,
        dpi: 300,
        antiAliasing: true,
        hardCapMP: 80,
        oversizePolicy: "confirm",
        chunkSize: 5,
        chunkSleepMs: 20,
        enableMemoryRelief: true,
        oversizedPreviewMax: 8,
        showChunkToast: true,
        chunkToastMs: 2000,
        visibleOnly: true,
        unlockedOnly: true
      }
    }
  };

  const loadExample = useCallback(() => {
    setJsonInput(JSON.stringify(defaultExample, null, 2));
    setError(null);
    setWarnings([]);
    setLastResponse(null);
    setValidationResult(null);
  }, []);

  /**
   * Normaliza o JSON de entrada para o formato esperado pelo sistema
   */
  const normalizeAction = useCallback((parsed: any): NormalizedAction => {
    // Se já está no formato correto, retorna como está
    if (parsed.action && parsed.data) {
      return {
        action: parsed.action,
        data: parsed.data,
        requestId: parsed.requestId
      };
    }

    // Se é um formato antigo (só action e data no root), normaliza
    if (parsed.action && !parsed.data) {
      const { action, requestId, ...data } = parsed;
      return {
        action,
        data,
        requestId
      };
    }

    // Fallback: assume que tudo é data e action é undefined
    return {
      action: parsed.action || 'unknown',
      data: parsed,
      requestId: parsed.requestId
    };
  }, []);

  /**
   * Valida e normaliza o JSON de entrada
   */
  const validateAndNormalizeJson = useCallback((jsonString: string): ValidationResult => {
    const warnings: string[] = [];
    
    try {
      const parsed = JSON.parse(jsonString);
      
      // Validações básicas
      if (!parsed || typeof parsed !== 'object') {
        return { valid: false, error: 'JSON deve ser um objeto válido' };
      }
      
      if (!parsed.action) {
        return { valid: false, error: 'Campo "action" é obrigatório' };
      }
      
      if (typeof parsed.action !== 'string') {
        return { valid: false, error: 'Campo "action" deve ser uma string' };
      }

      // Normaliza a estrutura
      const normalized = normalizeAction(parsed);

      // Validações específicas por tipo de ação
      if (normalized.action.startsWith('corelworks:tool')) {
        if (!normalized.data.toolId) {
          return { valid: false, error: 'Campo "toolId" é obrigatório para ações corelworks:tool' };
        }
      }

      // Warnings para campos que serão removidos/ignorados
      if (parsed.sessionToken && parsed.sessionToken !== '<TOKEN>') {
        warnings.push('sessionToken será injetado automaticamente pelo sistema');
      }
      
      if (parsed.auth && typeof parsed.auth === 'object') {
        warnings.push('auth será injetado automaticamente em produção');
      }

      if (parsed.requestId && !parsed.requestId.startsWith('req-')) {
        warnings.push('requestId será gerado automaticamente se não seguir o padrão');
      }

      return { 
        valid: true, 
        normalized, 
        warnings: warnings.length > 0 ? warnings : undefined 
      };
    } catch (e) {
      return { valid: false, error: `JSON inválido: ${(e as Error).message}` };
    }
  }, [normalizeAction]);

  /**
   * Valida o JSON em tempo real conforme o usuário digita
   */
  const validateJsonRealTime = useCallback((jsonString: string) => {
    if (!jsonString.trim()) {
      setValidationResult(null);
      setError(null);
      setWarnings([]);
      return;
    }

    const result = validateAndNormalizeJson(jsonString);
    setValidationResult(result);
    
    if (result.valid) {
      setError(null);
      setWarnings(result.warnings || []);
    } else {
      setError(result.error || 'Erro de validação');
      setWarnings([]);
    }
  }, [validateAndNormalizeJson]);

  /**
   * Verifica e garante que o sessionToken está disponível
   */
  const ensureSessionToken = useCallback(async (): Promise<boolean> => {
    // Verifica se já existe token
    const currentToken = (window as any)?.sessionToken;
    if (currentToken) {
      sendToTerminal('🔧 [DEBUGGER] SessionToken encontrado:', { token: currentToken.substring(0, 10) + '...' });
      return true;
    }

    sendToTerminal('🔧 [DEBUGGER] SessionToken não encontrado, iniciando handshake...');
    
    // ✅ ATUALIZADO: Usando SessionService centralizado
    const result = await SessionService.ensureToken({ timeoutMs: 2 * 60 * 1000 }); // 2 min
    
    if (!result.success) {
      sendToTerminal('❌ [DEBUGGER] Timeout ao aguardar token', { elapsedMs: result.elapsedMs });
      return false;
    }

    sendToTerminal('✅ [DEBUGGER] SessionToken obtido', { elapsedMs: result.elapsedMs });
    return true;
  }, []);

  const handleSend = useCallback(async () => {
    if (!jsonInput.trim()) {
      setError('Por favor, insira um JSON válido');
      return;
    }

    // Usa a validação já feita em tempo real ou faz uma nova
    let validation = validationResult;
    if (!validation) {
      validation = validateAndNormalizeJson(jsonInput);
    }

    if (!validation.valid) {
      setError(validation.error || 'JSON inválido');
      return;
    }

    if (!validation.normalized) {
      setError('Erro interno: ação não foi normalizada corretamente');
      return;
    }

    setIsLoading(true);
    setError(null);
    setLastResponse(null);

    try {
      // Verifica se a ação precisa de token
      // ⚠️ CONTRATO (03/12/2025): Conforme backend C#
      // - app-status e health:pong: EXIGEM sessionToken (mas são isentas de HMAC)
      // - session:hello, tools:bootstrap-session, open-tools-docker: isentas de sessionToken (mas exigem HMAC)
      const { action } = validation.normalized;
      const needsToken = !['session:hello', 'tools:bootstrap-session', 'open-tools-docker'].includes(action) 
        && !action.toLowerCase().startsWith('debug:');
      
      if (needsToken) {
        sendToTerminal('🔧 [DEBUGGER] Verificando sessionToken...');
        const hasToken = await ensureSessionToken();
        
        if (!hasToken) {
          setError('Não foi possível obter sessionToken. Verifique se o CorelDraw está aberto e conectado.');
          setIsLoading(false);
          return;
        }
      }

      const { data } = validation.normalized;
      
      // Log quando skipCache está ativo
      if (skipCache) {
        sendToTerminal('🔄 [DEBUGGER] Skip Cache ATIVO: usando código da caixa de texto (ignorando qualquer cache)');
      }
      
      sendToTerminal('🔧 [DEBUGGER] Enviando ação normalizada:', {
        original: JSON.parse(jsonInput),
        normalized: validation.normalized,
        warnings: validation.warnings,
        hasToken: needsToken ? !!((window as any)?.sessionToken) : 'N/A',
        skipCache: skipCache
      });

      const requestId = corelCommunication.sendActionGuarded(
        action,
        data,
        (response) => {
          const debugResponse: DebugResponse = {
            ...response,
            timestamp: new Date().toISOString()
          };
          
          setLastResponse(debugResponse);
          setIsLoading(false);
          
          sendToTerminal('🔧 [DEBUGGER] Resposta recebida:', debugResponse);
          
          // Se recebeu erro de token inválido, tenta renovar
          if (response.status === 'erro' && response.message?.includes('Token de sessão inválido')) {
            sendToTerminal('🔄 [DEBUGGER] Token inválido detectado, tentando renovar...');
            setTimeout(() => {
              // ✅ ATUALIZADO: Usando SessionService
              SessionService.sendHello({ timestamp: Date.now() });
            }, 1000);
          }
        }
      );

      if (!requestId) {
        setError('Falha ao enviar mensagem. Verifique se o WebView está disponível e se você tem permissão.');
        setIsLoading(false);
      }
    } catch (e) {
      const errorMessage = `Erro ao enviar: ${(e as Error).message}`;
      setError(errorMessage);
      setIsLoading(false);
      sendToTerminal('❌ [DEBUGGER] Erro ao enviar:', { error: errorMessage, input: jsonInput });
    }
  }, [jsonInput, validationResult, validateAndNormalizeJson, ensureSessionToken, skipCache]);

  const copyResponse = useCallback(async () => {
    if (!lastResponse) return;
    
    try {
      await navigator.clipboard.writeText(JSON.stringify(lastResponse, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      sendToTerminal('❌ [DEBUGGER] Erro ao copiar', { error: e instanceof Error ? e.message : String(e) });
    }
  }, [lastResponse]);

  /**
   * Exporta o código original enviado para um arquivo .json
   * - Inclui o JSON original com placeholders para campos sensíveis
   * - Adiciona metadados da execução bem-sucedida
   * - Usa nome de arquivo com prefixo de versão: v1.0.0__<slug>.json
   */
  const exportActionJson = useCallback(() => {
    try {
      if (!jsonInput.trim()) {
        setError('Nada para exportar: JSON de entrada vazio.');
        return;
      }

      if (!lastResponse || lastResponse.status !== 'ok') {
        setError('Só é possível exportar códigos que executaram com sucesso.');
        return;
      }

      // Garante que temos a ação normalizada para extrair metadados
      const result = validationResult ?? validateAndNormalizeJson(jsonInput);
      if (!result.valid || !result.normalized) {
        setError(result.error || 'JSON inválido para exportação');
        return;
      }

      // Parse do JSON original para criar versão com placeholders
      const originalJson = JSON.parse(jsonInput);
      const codeWithPlaceholders = { ...originalJson };

      // Substitui campos sensíveis por placeholders
      if (codeWithPlaceholders.sessionToken) {
        codeWithPlaceholders.sessionToken = '{{sessionToken}}';
      }
      if (codeWithPlaceholders.auth) {
        codeWithPlaceholders.auth = '{{auth}}';
      }
      if (codeWithPlaceholders.requestId) {
        codeWithPlaceholders.requestId = '{{requestId}}';
      }

      // Melhora formatação do código VB.NET se existir
      if (codeWithPlaceholders.data?.code && typeof codeWithPlaceholders.data.code === 'string') {
        // Converte \n para quebras de linha reais para melhor legibilidade
        codeWithPlaceholders.data.code = codeWithPlaceholders.data.code
          .replace(/\\n/g, '\n')
          .replace(/\\t/g, '\t')
          .replace(/\\"/g, '"')
          .replace(/\\\\/g, '\\');
      }

      // Deriva nome base a partir de toolId (quando existir) ou da action
      const toolId = result.normalized.data?.toolId;
      const baseName = toolId || result.normalized.action || 'acao';

      // Slug simples
      const slug = String(baseName)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '');

      const version = '1.0.0';
      const fileName = `v${version}__${slug}.json`;

      // Mantemos simples: sem timestamps no arquivo exportado
      
      // Extrai código VB.NET se existir para formatação separada
      const vbnetCode = codeWithPlaceholders.data?.code;
      const hasVbnetCode = vbnetCode && typeof vbnetCode === 'string' && vbnetCode.trim().length > 0;
      
      // Estrutura base exportada: action pronta para envio
      const actionExport = {
        action: 'vbcompiler',
        data: {
          vbid: (toolId ? String(toolId) : slug.replace(/-/g, '_')) || 'convert_object',
          code: hasVbnetCode ? vbnetCode : (codeWithPlaceholders.data?.code || ''),
          parameters: {},
          progress: false,
          progressEvery: 0,
          progressLabel: 'Conversão/Bitmap',
          progressIntervalMs: 0
        }
      };

      const payload = exportSnapshotEnabled ? {
        ...actionExport,
        jsonPayload: codeWithPlaceholders,
        originalInput: jsonInput,
        execution: {
          status: lastResponse.status,
          requestId: lastResponse.requestId,
          timestamp: lastResponse.timestamp,
          message: lastResponse.message
        }
      } : actionExport;

      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      sendToTerminal('✅ [DEBUGGER] Exportação realizada com sucesso', { fileName, action: actionExport.action, vbid: actionExport.data.vbid, snapshot: exportSnapshotEnabled });
    } catch (e) {
      const msg = (e as Error).message;
      setError(`Falha ao exportar: ${msg}`);
      sendToTerminal('❌ [DEBUGGER] Falha na exportação', { error: msg });
    }
  }, [jsonInput, lastResponse, validationResult, validateAndNormalizeJson]);

  const clearAll = useCallback(() => {
    setJsonInput('');
    setLastResponse(null);
    setError(null);
    setWarnings([]);
    setValidationResult(null);
  }, []);

  const handleClearStorage = useCallback(() => {
    try {
      TokenService.clearAllStorage();
      sendToTerminal('🗑️ [DEBUGGER] Storage limpo com sucesso');
      setError(null);
      setWarnings(['Storage limpo - faça login novamente para testar']);
    } catch (error) {
      setError(`Erro ao limpar storage: ${error}`);
    }
  }, []);

  /**
   * Manipula mudanças no input com validação em tempo real
   */
  const handleInputChange = useCallback((value: string) => {
    setJsonInput(value);
    validateJsonRealTime(value);
  }, [validateJsonRealTime]);

  // Atalhos de teclado
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Enter ou Cmd+Enter: Enviar
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (!isLoading && jsonInput.trim() && validationResult?.valid) {
          handleSend();
        }
      }
      // Ctrl+K ou Cmd+K: Limpar
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        if (!isLoading) {
          clearAll();
        }
      }
      // Ctrl+E ou Cmd+E: Carregar exemplo
      if ((e.ctrlKey || e.metaKey) && e.key === 'e') {
        e.preventDefault();
        if (!isLoading) {
          loadExample();
        }
      }
      // Ctrl+/ ou Cmd+/: Toggle skipCache
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        const newValue = !skipCache;
        setSkipCache(newValue);
        try {
          localStorage.setItem('debugger:skipCache', String(newValue));
        } catch {}
        sendToTerminal(`🔄 [DEBUGGER] Skip Cache ${newValue ? 'ATIVADO' : 'DESATIVADO'}: ${newValue ? 'Sempre buscará código fresco da API/arquivo' : 'Usará cache quando disponível'}`);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLoading, jsonInput, validationResult, skipCache, handleSend, clearAll, loadExample]);

  return (
    <div className="w-full h-full bg-navy-800 border border-navy-600/50 overflow-hidden flex flex-col" style={{ minWidth: '280px', minHeight: '100vh' }}>
      {/* Header */}
      <Header onBack={onClose} />

      {/* Content - Split View: Editor e Resposta lado a lado em desktop */}
      <div className="flex-1 flex flex-col sm:flex-row min-h-0 overflow-hidden">
        {/* Input Section - Lado esquerdo */}
        <div className="flex-1 flex flex-col min-h-0 sm:max-w-[50%]">
          <JsonEditor
            value={jsonInput}
            onChange={handleInputChange}
            onExample={loadExample}
            onClear={clearAll}
            onOpenNormalize={() => setShowNormalize(true)}
            isLoading={isLoading}
            validationResult={validationResult}
            warnings={warnings}
            error={error}
          />
        </div>

        {/* Response Section - Lado direito */}
        <div className="flex-1 flex flex-col min-h-0 sm:max-w-[50%]">
          <ResponsePane
            isLoading={isLoading}
            lastResponse={lastResponse}
            copied={copied}
            onCopy={copyResponse}
            onExport={exportActionJson}
            canExport={!!jsonInput.trim()}
            exportModeToggle={<ExportModeToggle enabled={exportSnapshotEnabled} onToggle={setExportSnapshotEnabled} />}
          />
        </div>
      </div>

      {/* Action Bar - Fixo na parte inferior */}
      <ActionBar
        onSend={handleSend}
        onHandshake={async () => {
          setError(null);
          sendToTerminal('🔧 [DEBUGGER] Forçando handshake de sessão...');
          const success = await ensureSessionToken();
          if (success) {
            sendToTerminal('✅ [DEBUGGER] Handshake realizado com sucesso');
          } else {
            setError('Falha no handshake. Verifique se o CorelDraw está aberto.');
          }
        }}
        onClearStorage={handleClearStorage}
        disabledSend={isLoading || !jsonInput.trim() || (validationResult ? !validationResult.valid : false)}
        isLoading={isLoading}
        skipCache={skipCache}
        onToggleSkipCache={(enabled) => {
          setSkipCache(enabled);
          try {
            localStorage.setItem('debugger:skipCache', String(enabled));
          } catch {}
          sendToTerminal(`🔄 [DEBUGGER] Skip Cache ${enabled ? 'ATIVADO' : 'DESATIVADO'}: ${enabled ? 'Sempre buscará código fresco da API/arquivo' : 'Usará cache quando disponível'}`);
        }}
      />

      {/* Footer */}
      <Footer />
      {showNormalize && (
        <NormalizeModal
          onClose={() => setShowNormalize(false)}
          onInsert={(payloadJson) => setJsonInput(payloadJson)}
        />
      )}
    </div>
  );
}

// Alias para compatibilidade com importações existentes
export { DebuggerPage as DebuggerModal };
